import React, { useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useDebounceFn } from "ahooks";
import { useTranslation } from "@/langs/useTranslation";
import { isPromise } from "@/helpers/isPromise";

const styles = theme => ({
  root: {
    padding: theme.spacing(0, 0, 3)
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
});

const useModalStyles = makeStyles(theme => ({
  buttonProgress: {
    marginRight: theme.spacing(0.75)
  }
}));

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      {children && <Typography variant="h6">{children}</Typography>}
      <IconButton
        aria-label="close"
        className={classes.closeButton}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles(theme => ({
  root: {
    overflow: "visible",
    padding: theme.spacing(0)
  }
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
  root: {
    padding: theme.spacing(3, 0, 0),
    justifyContent: "center",

    "& .MuiButton-contained": {
      minWidth: 90
    }
  }
}))(MuiDialogActions);

function Modal({
  title,
  titleStyle,
  width = 400,
  scroll = "body",
  open,
  closeable,
  dividers,
  children,
  footer,
  showConfirm = true,
  showCancel = true,
  onClose,
  onCancel,
  onConfirm,
  confirmText,
  cancelText
}) {
  const { t } = useTranslation();
  const classes = useModalStyles();
  const [loading, setLoading] = useState(false);

  const { run: handleConfirm } = useDebounceFn(
    async () => {
      if (loading) return;
      if (onConfirm) {
        const res = onConfirm();
        if (isPromise(res)) {
          try {
            setLoading(true);
            await res;
            // onClose?.();
          } finally {
            setLoading(false);
          }
        }
      }
      // onClose?.();
    },
    { leading: true, trailing: false }
  );

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  return (
    <Dialog
      scroll={scroll}
      onClose={onClose}
      open={open}
      maxWidth={false}
      className={classes.root}
    >
      {!title && !closeable ? null : (
        <DialogTitle onClose={onClose} style={titleStyle}>{title}</DialogTitle>
      )}
      <DialogContent style={{ width }} dividers={dividers}>
        {children}
      </DialogContent>
      {footer === undefined && (
        <DialogActions>
          {showConfirm && (
            <Button
              variant="contained"
              autoFocus
              color="primary"
              onClick={handleConfirm}
              style={loading ? { cursor: "default", opacity: 0.6 } : undefined}
              // disabled={loading}
              startIcon={
                loading && (
                  <CircularProgress
                    color="inherit"
                    size={16}
                    className={classes.buttonProgress}
                  />
                )
              }
            >
              {confirmText || t({ defaultMessage: "确认" })}
            </Button>
          )}
          {showCancel && (
            <Button variant="contained" onClick={handleCancel}>
              {cancelText || t({ defaultMessage: "取消" })}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

const isEqual = (prevProps, nextProps) => {
  if (nextProps.open) {
    return false;
  }
  return nextProps.open === prevProps.open;
};

export default React.memo(Modal, isEqual);
